import React, { useEffect, useState, Suspense } from "react";
import { styled } from "../../stitches.config";
import { h1, bodyLarge } from "../commonStyles/Fonts";
import SubscribeButton from "../SubscribeButton";
import AuthorLinkIcon from "../assets/icons/author-link-icon.svg";
import { useStores } from "../../helpers";
import { observer } from "mobx-react";
import { observable, runInAction } from "mobx";
import Loader from "../../components/Loader";
import HtmlParser from "../../components/Article/Content/HtmlParser";
import Title from "../atoms/Title";
import ArticleListItem from "../components/ArticleListItem";
import RecruitmentBox from "../components/RecruitmentBox";
import Media from "../../components/common/Media";
import Button from "../atoms/Button";
import { Sticky, StickyContainer } from "react-sticky";
import Breadcrumbs from "../components/Breadcrumbs";
import useMediaQuery from '../../helpers/useMediaQuery';

import { DISABLE_PREMIUM_FLAG_ACTIVE, IS_FEATURE_TIP } from "../../components/common/constants/constants";
import { ModalType } from "../Modal/config";
import ModalLink from "../Modal/ModalLink";
import { PaymentStatus, PaymentType } from "../types/Payment";
import TipCallToAction from "../Modal/payment/TipCallToAction";
import TipFormStandalone from "../Modal/payment/TipFormStandalone";
import { useParams } from "react-router-dom";
import SlateRenderer from "../../components/Article/Parts/Slate/SlateRenderer";

import PageCover from '../components/PageCover';
import Footer from "../components/Footer"

interface ILastPosts {
  title: string;
  lead: string;
  category: {
    name: string;
  };
  picture: {
    img: string;
    alt: string;
  };
  url: string;
}

const AuthorPage: React.FC = observer(() => {
  const { UserStore } = useStores();
  const [complexState] = useState(() => observable(UserStore));
  const [stateMobx] = useState(() =>
    observable({
      nextPage: "",
      pending: false,
      hideButton: false,
      posts: [] as ILastPosts[],
    })
  );

  const isDesktop = useMediaQuery('bp4');
  const params = useParams();

  // dla SSR musi się wykonać także poza useEffect
  const { slug } = params;
  UserStore.updateSlug(slug);

  useEffect(() => {
    if (params?.slug) {
      UserStore.updateSlug(params.slug);
    }
  }, [params]);

  // resetujemy stan artykułów po zmianie autora
  useEffect(() => {
    runInAction(() => {
      stateMobx.posts = [];
      stateMobx.nextPage = "";
      stateMobx.hideButton = false;
      stateMobx.pending = false;
    });
  }, [slug]);

  useEffect(() => {
    const user = complexState.fetchedUser?.user;

    if (
      user &&
      typeof user.url === "string" &&
      user.url.includes(slug) &&
      stateMobx.posts.length === 0 &&
      user.last_posts
    ) {
      runInAction(() => {
        stateMobx.posts = user.last_posts;
        stateMobx.nextPage = `${window.API_HOST}/pwa-user/${slug}?to_date=${encodeURIComponent(
          complexState.fetchedUser.LAST_POSTS_LAST_DATE
        )}`;
      });
    }
  }, [slug, complexState.fetchedUser]);

  const { user } = complexState.fetchedUser;

  if (complexState.status === "loading") {
    return <Loader />;
  }

  const fetchPosts = async ({ url }: { url: string }) => {
    if (!url || stateMobx.pending) return;

    runInAction(() => {
      stateMobx.pending = true;
    });

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Błąd podczas pobierania danych");
      const data = await res.json();

      if (!data?.user?.last_posts?.length) {
        runInAction(() => {
          stateMobx.hideButton = true;
        });
        return;
      }

      runInAction(() => {
        stateMobx.posts = [...stateMobx.posts, ...data.user.last_posts];

        if (typeof data.LAST_POSTS_LAST_DATE === "undefined") {
          stateMobx.hideButton = true;
        } else {
          stateMobx.nextPage = `${window.API_HOST}/pwa-user/${complexState.currentSlug
            }?to_date=${encodeURIComponent(data.LAST_POSTS_LAST_DATE)}`;
        }
      });
    } catch (err) {
      console.error("Fetch posts error:", err);
    } finally {
      runInAction(() => {
        stateMobx.pending = false;
      });
    }
  };

  return (
    <>
      <PageCover
        beforeContent={
          <Breadcrumbs
            category={{ name: "Skład redakcji", link: "/redakcja" }}
            subCategory={{ name: user.name, link: "" }}
            allWhite
          />
        }
        title={
          <>
            {user.name}
            {!DISABLE_PREMIUM_FLAG_ACTIVE && (
              <Suspense fallback={null}>
                <SubscribeButton
                  size="extraBig"
                  subscribed={false}
                  authorId={+user.OID}
                />
              </Suspense>
            )}
          </>
        }
        description={user.aboutWysiwyg}
        isDescriptionWyswig={true}
        image={user.img}
      />
      <Container>
        <ContentWrapper>
          <ArticlesSection>
            <Title withDots size="large">
              Moje artykuły
            </Title>

            {stateMobx.posts?.map(
              ({ title, lead, category, picture, url }: ILastPosts, index) => (
                <React.Fragment key={`${url}-${index}`}>
                  <ArticleListItem
                    title={title}
                    lead={lead}
                    url={url}
                    categoryName={category.name}
                    picture={picture}
                  />
                  {index !== stateMobx.posts.length - 1 && <ListSeparator />}
                  {index === 1 &&
                    IS_FEATURE_TIP &&
                    user.isRecentlyActive && (
                      <>
                        <TipFormStandalone
                          allAuthors={[user.OID]}
                          descriptionText="Teraz możesz docenić pracę dziennikarzy i dziennikarek. Cała kwota trafi do nich. Wraz z napiwkiem możesz przekazać też krótką wiadomość."
                          origin="tip-standalone-author"
                        />
                        <ListSeparator />
                      </>
                    )}
                </React.Fragment>
              )
            )}

            {!stateMobx.hideButton && (
              <ButtonWrapper>
                <Button
                  text={stateMobx.pending ? "Ładowanie..." : "Pokaż więcej"}
                  starPosition="none"
                  disabled={stateMobx.pending}
                  onClick={() => fetchPosts({ url: stateMobx.nextPage })}
                />
              </ButtonWrapper>
            )}
          </ArticlesSection>

          <SideSection>
            <StickyContainer style={{ height: "100%" }}>
              <Sticky topOffset={-91} style={{ height: "100%" }}>
                {({ style, isSticky }) => (
                  <StickyWrap isSticky={isSticky} style={style}>
                    <RecruitmentBox />
                  </StickyWrap>
                )}
              </Sticky>
            </StickyContainer>
          </SideSection>
        </ContentWrapper>
      </Container>
      <Footer />
    </>
  );
});

const StickyWrap = styled("div", {
  variants: {
    isSticky: {
      true: {
        paddingTop: "91px",
      },
      false: {},
    },
  },
});

const Container = styled("div", {
  margin: "0 auto",
  padding: "0 16px",
  "@bp3": {
    width: "960px",
  },
  "@bp4": {
    padding: "0 36px",
    width: "1160px",
  },
  "@bp6": {
    width: "1344px",
  },
});

const ContentWrapper = styled("div", {
  display: "flex",
  flexDirection: "column-reverse",
  "@bp4": {
    display: "grid",
    gridTemplateColumns: "1fr 300px",
    alignItems: "top",
    marginTop: "33px",
  },
});

const ListSeparator = styled("span", {
  display: "block",
  width: "100%",
  margin: "12px 0px 24px 0",
  "@bp4": {
    height: "1px",
    backgroundColor: "#E5E5E5",
    margin: "40px 0",
  },
});

const ArticlesSection = styled("div", {
  marginBottom: "40px",
  maxWidth: "840px",
});

const SideSection = styled("div", {
  display: "none",
  height: "100%",
  "@bp4": {
    display: "block",
    alignSelf: "flex-start",
    paddingTop: "58px",
  },
});

const ButtonWrapper = styled("div", {
  margin: "40px auto",
  display: "flex",
  justifyContent: "center",
});

export default AuthorPage;
